import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { isCancel } from "axios";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LineChart, AlertTriangle, X } from "lucide-react";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./TraficPage.module.css";
import DashboardAnalytics from "../../components/Layout/DashboardAnalytics/DashboardAnalytics";
import DashboardGeoMap from "../../components/Layout/DashboardGeoMap/DashboardGeoMap";
import ProjectPickerCarousel from "../../components/Layout/ProjectPickerCarousel/ProjectPickerCarousel";
import { getPlanStableId } from "../../data/dashboard-projects-dummy";
import { useAuth } from "../../context/authContext";
import {
  getUserSubscriptionPlans,
  getSubscriptionPlans,
} from "../../services/subscription-plan-service";
import {
  fetchSearchConsoleAnalytics,
  startSearchConsoleOAuth,
  clearAndReconnectSearchConsole,
} from "../../services/search-console-service";

export default function TraficPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["common", "dashboard"]);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [gscRange, setGscRange] = useState("30d");
  const gscFetchSeq = useRef(0);
  const gscLastProjectRef = useRef(null);
  const [gscState, setGscState] = useState({
    loading: false,
    data: null,
    error: null,
  });

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      if (!user) {
        setPlans([]);
        return;
      }
      const isStaff =
        user.role === "admin" || user.role === "owner";
      if (isStaff) {
        const res = await getSubscriptionPlans();
        const planList =
          res.success && Array.isArray(res.data) ? res.data : [];
        setPlans(planList);
      } else {
        const plansData = await getUserSubscriptionPlans();
        const planList = plansData.success ? plansData.data || [] : [];
        setPlans(planList);
      }
    } catch {
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans, location.key]);

  useEffect(() => {
    if (!plans.length) {
      setSelectedProjectId(null);
      return;
    }
    setSelectedProjectId((prev) => {
      const ids = plans.map((p, i) => getPlanStableId(p, i));
      if (prev && ids.includes(prev)) return prev;
      return ids[0] ?? null;
    });
  }, [plans]);

  useEffect(() => {
    if (location.pathname !== "/traffic") return;

    const onVisibility = () => {
      if (document.visibilityState === "visible" && user) {
        fetchPlans();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchPlans, location.pathname, user]);

  useEffect(() => {
    const ok = searchParams.get("gsc");
    const err = searchParams.get("gsc_error");
    if (ok || err) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!user || !selectedProjectId) {
      setGscState({ loading: false, data: null, error: null });
      gscLastProjectRef.current = null;
      return;
    }
    const ac = new AbortController();
    const seq = ++gscFetchSeq.current;

    const prevProj = gscLastProjectRef.current;
    const projectSwitched =
      prevProj != null && prevProj !== selectedProjectId;

    setGscState((prev) => ({
      loading: true,
      data: projectSwitched ? null : prev.data,
      error: null,
    }));
    gscLastProjectRef.current = selectedProjectId;

    (async () => {
      try {
        const data = await fetchSearchConsoleAnalytics(
          selectedProjectId,
          gscRange,
          { signal: ac.signal }
        );
        if (seq !== gscFetchSeq.current) return;
        setGscState({ loading: false, data, error: null });
      } catch (e) {
        if (isCancel(e)) return;
        if (seq !== gscFetchSeq.current) return;
        const code = e?.response?.data?.code;
        const message = e?.response?.data?.message;
        setGscState({
          loading: false,
          data: null,
          error: { code, message, status: e?.response?.status },
        });
      }
    })();
    return () => {
      ac.abort();
    };
  }, [user, selectedProjectId, gscRange, location.key]);

  const gscLivePayload = gscState.data?.source === "search_console";

  const gscGeoLive =
    gscLivePayload &&
    gscState.data?.trafficByIso3 &&
    Object.keys(gscState.data.trafficByIso3).length > 0
      ? gscState.data.trafficByIso3
      : null;

  const gscGeoCountries =
    gscLivePayload && gscState.data?.countries?.length
      ? gscState.data.countries
      : null;

  const useLiveGscMap = gscLivePayload;

  const gscErrMsg = String(gscState.error?.message || "").toLowerCase();
  const gscPermissionOrStaleToken =
    gscState.error &&
    (gscErrMsg.includes("sufficient permission") ||
      gscErrMsg.includes("insufficient permission") ||
      gscErrMsg.includes("permission denied") ||
      gscState.error.status === 403);

  const gscReconnectDismissKey = useMemo(() => {
    const p = selectedProjectId || "none";
    const preset = gscRange || "30d";
    return `gsc_reconnect_dismissed:${p}:${preset}`;
  }, [selectedProjectId, gscRange]);

  const [hideReconnectHint, setHideReconnectHint] = useState(() => {
    try {
      return sessionStorage.getItem(gscReconnectDismissKey) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      setHideReconnectHint(sessionStorage.getItem(gscReconnectDismissKey) === "1");
    } catch {
      setHideReconnectHint(false);
    }
  }, [gscReconnectDismissKey]);

  if (loading) {
    return (
      <div className={portalStyles.portalPage}>
        <div className={portalStyles.container}>
          <PortalShellPageHeader
            icon={
              <LineChart size={28} strokeWidth={1.75} absoluteStrokeWidth />
            }
            title={t("dashboard:trafficPageTitle")}
            subtitle={t("dashboard:loadingData")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={
            <LineChart size={28} strokeWidth={1.75} absoluteStrokeWidth />
          }
          title={t("dashboard:trafficPageTitle")}
          subtitle={t("dashboard:trafficPageSubtitle")}
        />

        {selectedProjectId && gscState.error?.code === "GSC_NOT_CONNECTED" ? (
          <div className={styles.gscConnectBanner}>
            <div className={styles.gscConnectText}>
              <p className={styles.gscConnectTitle}>
                {t("dashboard:analytics.gscNotConnected")}
              </p>
              <p className={styles.gscConnectHint}>
                {t("dashboard:analytics.connectGscHint")}
              </p>
            </div>
            <button
              type="button"
              className={styles.gscConnectButton}
              onClick={() => startSearchConsoleOAuth()}
            >
              {t("dashboard:analytics.connectGscButton")}
            </button>
          </div>
        ) : null}

        {selectedProjectId && gscPermissionOrStaleToken ? (
          hideReconnectHint ? null : (
            <div className={styles.gscReconnectHintRow} role="status">
              <div className={styles.gscReconnectHintIcon} aria-hidden="true">
                <AlertTriangle size={20} strokeWidth={2} />
              </div>
              <div className={styles.gscReconnectHintBody}>
                <p className={styles.gscReconnectHintTitle}>
                  {t("dashboard:analytics.gscStaleTokenTitle")}
                </p>
                <p className={styles.gscReconnectHintDesc}>
                  {t("dashboard:analytics.gscStaleTokenHint")}
                </p>
              </div>
              <div className={styles.gscReconnectHintActions}>
                <button
                  type="button"
                  className={styles.gscReconnectHintPrimary}
                  onClick={() => clearAndReconnectSearchConsole()}
                >
                  {t("dashboard:analytics.gscReconnectButton")}
                </button>
                <button
                  type="button"
                  className={styles.gscReconnectHintDismiss}
                  aria-label={t("common:close")}
                  onClick={() => {
                    try {
                      sessionStorage.setItem(gscReconnectDismissKey, "1");
                    } catch {}
                    setHideReconnectHint(true);
                  }}
                >
                  <X size={18} strokeWidth={2} aria-hidden />
                </button>
              </div>
            </div>
          )
        ) : null}

        <div
          className={`${styles.trafficTopRow} ${
            selectedProjectId
              ? styles.trafficTopRowTwoCol
              : styles.trafficTopRowOneCol
          }`}
        >
          <section
            className={styles.carouselSection}
            aria-label={t("dashboard:trafficCarouselSection")}
          >
            <div className={styles.carouselWrap}>
              <ProjectPickerCarousel
                embedded
                fullWidthEmbeddedViewport
                matchProductsHeight={!!selectedProjectId}
                plans={plans}
                selectedPlanId={selectedProjectId}
                onSelect={setSelectedProjectId}
              />
            </div>
          </section>
          {selectedProjectId ? (
            <aside className={styles.geoMapAside}>
              <DashboardGeoMap
                projectId={selectedProjectId}
                fillColumn
                liveGscCountries={gscGeoCountries}
                liveTrafficByIso3={gscGeoLive}
                useLiveGsc={useLiveGscMap}
                rangeDays={gscState.data?.range?.days ?? 30}
                gscRangePreset={gscState.data?.range?.preset ?? gscRange}
              />
            </aside>
          ) : null}
        </div>

        {selectedProjectId ? (
          <section
            className={portalStyles.trafficSection}
            aria-labelledby="traffic-analytics-heading"
          >
            <div className={styles.trafficAnalyticsHeadingRow}>
              <h2
                id="traffic-analytics-heading"
                className={`${portalStyles.sectionLabel} ${styles.analyticsHeadingTitle}`}
              >
                {t("dashboard:sectionTrafficMonitoring")}
              </h2>
              <div
                className={styles.gscRangeGroup}
                role="group"
                aria-label={t("dashboard:gscRange.ariaLabel")}
              >
                {(["24h", "7d", "30d", "180d", "365d"]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`${styles.gscRangeBtn} ${
                      gscRange === key ? styles.gscRangeBtnActive : ""
                    }`}
                    onClick={() => setGscRange(key)}
                    aria-pressed={gscRange === key}
                  >
                    {t(`dashboard:gscRange.${key}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className={portalStyles.analyticsStack}>
              <DashboardAnalytics
                projectId={selectedProjectId}
                gscState={gscState}
                analyticsRangeKey={`${selectedProjectId}-${gscRange}`}
                rangePreset={gscRange}
              />
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
